import { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { auditEmailReport } from "@/lib/api";

// Reusable "email me the branded PDF report" capture block.
// Props: auditId (required), brand ("websitetech" | "in" | "kerala"), accent (tailwind bg class), theme colors.
export function AuditReportEmail({ auditId, report, brand = "websitetech", accentBg = "bg-brand", accentText = "text-brand" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const r = await auditEmailReport({
        ...(auditId ? { audit_id: auditId } : {}),
        ...(report ? { report } : {}),
        name: name.trim(), email: email.trim(),
        origin: window.location.origin, brand,
      });
      setDone(true);
      toast.success(r.message || "Report sent to your inbox.");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't send the report. Please try again.");
    }
    setSending(false);
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex items-start gap-3" data-testid="audit-email-sent">
        <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={22} />
        <div>
          <p className="font-heading font-bold text-slate-900">Check your inbox 📩</p>
          <p className="text-sm text-slate-600 mt-1">We've emailed your branded PDF audit report to <span className="font-semibold">{email}</span>.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white card-shadow p-6" data-testid="audit-email-form">
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} className={accentText} />
        <h3 className="font-heading text-lg font-bold text-slate-900">Email me the full PDF report</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">Get a branded PDF with every score and prioritized action — delivered to your inbox.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="audit-email-name"
          className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-3 text-slate-900 outline-none focus:border-slate-400 text-sm" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" type="email" data-testid="audit-email-input"
          className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-3 text-slate-900 outline-none focus:border-slate-400 text-sm" />
      </div>
      <button type="submit" disabled={sending} data-testid="audit-email-submit"
        className={`mt-3 w-full sm:w-auto ${accentBg} text-white font-semibold rounded-xl px-6 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:-translate-y-0.5 transition-transform`}>
        {sending ? <Loader2 className="animate-spin" size={18} /> : <><Mail size={16} /> Send my report</>}
      </button>
    </form>
  );
}

export default AuditReportEmail;
